import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tabs, Tab, Slider, Tooltip, Collapse, useTheme, alpha } from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import JoinInnerOutlinedIcon from "@mui/icons-material/JoinInnerOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDraftData } from "../context/DraftDataContext";
import { useMarketAdp } from "../helpers/useMarketAdp";
import { useAdp } from "../context/AdpContext";
import { getTeamsTableFromCsv } from "../helpers/chartHelpers";
import { groupDataByPlayer, normalizePlayerName, getMissingCombos, resolveMarketAdp } from "../helpers/playerHelpers";
import { analyzePortfolioStacks, DOME_STADIUMS } from "../services/stackDetection";
import { analyzePositionArchetypes, POSITION_ARCHETYPE_DESCRIPTIONS } from "../services/archetypeClassifier";
import HeroEmptyState from "../components/HeroEmptyState";
import StatCard from "../components/StatCard";
import { AccentCard, SectionHeader, PositionChip, TeamBadge, EmptyState, TeamDataUnavailable } from "../components/ui";
import { brand } from "../config/theme";
import { useStatusColors } from "../helpers/statusColors";
import { clvByPosition, clvByRoundBand, computeEntryDuplication, computeDupClusters, computeTournamentUniqueness } from "../helpers/portfolioInsights";

const SECTIONS = [
  { id: "stacks", label: "Stacks" },
  { id: "construction", label: "Construction" },
  { id: "gaps", label: "Gaps" },
  { id: "duplication", label: "Duplication" },
];

const POSITION_NAMES: Record<string, string> = {
  QB: "Quarterback", RB: "Running Back", WR: "Wide Receiver", TE: "Tight End",
};

// ── Stack report ────────────────────────────────────────────────────
// Concrete stack content instead of abstract pair statistics: the game
// stacks you actually built (grouped by NFL team, with dome/bring-back
// context) and the builds that drafted a QB naked.

interface StackGroup {
  nflTeam: string;
  qbName: string;
  buildCount: number;
  receivers: Array<{ name: string; position: string; count: number }>;
  /** Opposing teams you pair with this stack for playoff bring-backs. */
  bringBacks: string[];
  /** Playoff weeks where this stack lands a dome bring-back. */
  domeWeeks: number[];
  /** The builds carrying this stack. */
  entries: Array<{ draftId: string; label: string }>;
}

interface PlayoffWeek {
  week: number;
  label: string;   // "vs DAL" / "@ WAS" / "BYE"
  dome: boolean;
}

interface Wk17BringBack {
  opp: string;
  dome: boolean;
  /** Wk17-opponent players rostered on the stack's builds, most-stacked first. */
  players: Array<{ name: string; position: string; count: number }>;
}

// ── Expandable stack row ────────────────────────────────────────────
function StackRow({ g, totalBuilds, playoffWeeks, wk17bb, primary, onOpenPlayer, onViewBuilds }: {
  g: StackGroup;
  totalBuilds: number;
  playoffWeeks: PlayoffWeek[];
  /** Wk17 bring-backs actually built with this stack, most-stacked first. */
  wk17bb: Wk17BringBack | null;
  primary: string;
  onOpenPlayer: (name: string) => void;
  onViewBuilds: (qbName: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const coverage = totalBuilds > 0 ? g.buildCount / totalBuilds : 0;

  return (
    <Box sx={{ borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" } }}>
      {/* Collapsed row: click to expand */}
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: "flex", alignItems: "center", gap: 1.25, py: 1, cursor: "pointer",
          borderRadius: 1, "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <TeamBadge team={g.nflTeam} size="sm" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{g.qbName}</Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {g.receivers.slice(0, 3).map(r => `${r.name.split(" ").slice(-1)[0]} ×${r.count}`).join(" · ")}
              {g.receivers.length > 3 ? ` · +${g.receivers.length - 3}` : ""}
            </Typography>
          </Box>
          {/* Coverage: share of your builds carrying this stack */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
            <Box sx={{ width: 76, height: 5, borderRadius: 3, bgcolor: "action.hover", overflow: "hidden" }}>
              <Box sx={{ width: `${Math.min(coverage * 100, 100)}%`, height: "100%", bgcolor: primary, borderRadius: 3, opacity: 0.85 }} />
            </Box>
            <Typography sx={{ fontSize: 10, color: "text.disabled", fontVariantNumeric: "tabular-nums" }}>
              {Math.round(coverage * 100)}% of builds
            </Typography>
            {g.domeWeeks.length > 0 && (
              <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: primary }}>
                DOME Wk{[...g.domeWeeks].sort((a, b) => a - b).join("/")}
              </Typography>
            )}
            {wk17bb && wk17bb.players.length > 0 && (
              <Typography sx={{ fontSize: 10, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                Wk17 BB: <b>{wk17bb.players[0].name.split(" ").slice(-1)[0]} ×{wk17bb.players[0].count}</b> ({wk17bb.opp})
              </Typography>
            )}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
          {g.buildCount} {g.buildCount === 1 ? "build" : "builds"}
        </Typography>
        <Box component="span" sx={{
          color: "text.disabled", fontSize: 11, flexShrink: 0,
          transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s",
        }}>
          ▸
        </Box>
      </Box>

      {/* Expanded detail */}
      {open && (
        <Box sx={{ pb: 1.25, pl: 4.5, display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Playoff schedule for the stack's team */}
          {playoffWeeks.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "text.secondary" }}>
                Playoffs
              </Typography>
              {playoffWeeks.map(pw => (
                <Box key={pw.week} sx={{
                  fontSize: 10, fontWeight: 600, px: 0.875, py: 0.25, borderRadius: 999,
                  border: "1px solid", borderColor: pw.dome ? primary : "divider",
                  color: pw.dome ? primary : "text.secondary",
                  bgcolor: pw.dome ? alpha(primary, 0.08) : "transparent",
                }}>
                  Wk{pw.week} {pw.label}{pw.dome ? " · dome" : ""}
                </Box>
              ))}
            </Box>
          )}

          {/* Full partner list: click a player to open them */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "text.secondary" }}>
              Partners
            </Typography>
            {g.receivers.map(r => (
              <Box
                key={r.name}
                onClick={e => { e.stopPropagation(); onOpenPlayer(r.name); }}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.625,
                  border: "1px solid", borderColor: "divider", borderRadius: 999,
                  px: 1, py: 0.25, cursor: "pointer", "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <PositionChip position={r.position} size="sm" />
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{r.name}</Typography>
                <Typography sx={{ fontSize: 10.5, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>×{r.count}</Typography>
              </Box>
            ))}
          </Box>

          {/* Wk17 bring-backs actually built with this stack */}
          {wk17bb && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "text.secondary" }}>
                Wk17 bring-back · {wk17bb.opp}
              </Typography>
              {wk17bb.players.length === 0 ? (
                <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                  none built
                </Typography>
              ) : (
                wk17bb.players.map(p => (
                  <Box
                    key={p.name}
                    onClick={e => { e.stopPropagation(); onOpenPlayer(p.name); }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.625,
                      border: "1px solid", borderColor: "divider", borderRadius: 999,
                      px: 1, py: 0.25, cursor: "pointer", "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <PositionChip position={p.position} size="sm" />
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{p.name}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>×{p.count}</Typography>
                  </Box>
                ))
              )}
              {wk17bb.dome && (
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: primary }}>DOME</Typography>
              )}
            </Box>
          )}

          {/* The actual builds */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "text.secondary" }}>
              Builds
            </Typography>
            {g.entries.slice(0, 6).map(e => (
              <Box key={e.draftId} sx={{ fontSize: 10, px: 0.875, py: 0.25, borderRadius: 999, bgcolor: "action.selected", color: "text.secondary" }}>
                {e.label}
              </Box>
            ))}
            {g.entries.length > 6 && (
              <Typography sx={{ fontSize: 10, color: "text.disabled" }}>+{g.entries.length - 6} more</Typography>
            )}
            <Typography
              onClick={e => { e.stopPropagation(); onViewBuilds(g.qbName); }}
              sx={{ fontSize: 11, fontWeight: 600, color: primary, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              view in Teams →
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// "Premium" = roughly the first five rounds of a 12-team draft.
const PREMIUM_ADP_MAX = 60;

export default function InsightsPage() {
  const { rawData, filteredRawData, hasTeamData } = useDraftData();
  const { schedule, adpData } = useAdp();
  const { lookupForProvider } = useMarketAdp();
  const navigate = useNavigate();
  const theme = useTheme();
  const primary = theme.palette.mode === "dark" ? brand.primary.dark : brand.primary.light;
  const sc = useStatusColors();

  const [activeTab, setActiveTab] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const suppressObserver = useRef(false);

  const hasData = rawData.length > 0;

  // Teams (rosters) — computed once and reused for stacks, construction, and the by-draft list.
  const teams = useMemo(() => getTeamsTableFromCsv(filteredRawData), [filteredRawData]);
  const dupReport = useMemo(() => computeEntryDuplication(teams), [teams]);
  const [minDupShared, setMinDupShared] = useState(5);
  const tournamentUniqueness = useMemo(() => computeTournamentUniqueness(teams), [teams]);
  // Cluster drill-down: only the first (largest) tournament starts expanded.
  const [dupExpanded, setDupExpanded] = useState<Record<string, boolean>>({});
  const isDupExpanded = (tournament: string, index: number) =>
    dupExpanded[tournament] ?? index === 0;
  const toggleDup = (tournament: string, index: number) =>
    setDupExpanded(prev => ({ ...prev, [tournament]: !isDupExpanded(tournament, index) }));
  const dupClusters = useMemo(() => computeDupClusters(teams, minDupShared), [teams, minDupShared]);
  const dupClustersByTournament = useMemo(() => {
    const m = new Map<string, typeof dupClusters>();
    dupClusters.forEach(cl => {
      const list = m.get(cl.tournament) ?? [];
      list.push(cl);
      m.set(cl.tournament, list);
    });
    // biggest tournaments (by clustered entries) first
    return [...m.entries()].sort((a, b) =>
      b[1].reduce((s2, c) => s2 + c.entries.length, 0) - a[1].reduce((s2, c) => s2 + c.entries.length, 0));
  }, [dupClusters]);
  const positionByName = useMemo(() => {
    const m = new Map<string, string>();
    teams.forEach(t => t.players.forEach(p => { if (!m.has(p.name)) m.set(p.name, p.position); }));
    return m;
  }, [teams]);

  // ── Stacks (computed WITH schedule so bring-back/dome are correct) ──
  const stacks = useMemo(() => {
    if (!teams.length) return null;
    return analyzePortfolioStacks(
      teams,
      schedule ? { weekNums: schedule.weekNums, weekSchedules: schedule.weekSchedules } : undefined,
    );
  }, [teams, schedule]);

  // ── Construction: named archetype per position (QB/RB/WR/TE). Every draft
  // lands in exactly one bucket per position, so each sums to 100%. ──
  const positionDists = useMemo(() => analyzePositionArchetypes(teams), [teams]);

  // ── Tendencies: position timing vs market + CLV by round band ──
  const tendencyPicks = useMemo(() =>
    filteredRawData.map(r => ({
      position: r["Position"],
      pickNumber: Number(r["Pick Number"]),
      // Each pick scored against its own platform's market.
      marketAdp: resolveMarketAdp(
        lookupForProvider((r["Provider"] || "").trim()).get(
          normalizePlayerName(`${r["First Name"]} ${r["Last Name"]}`.trim())
        )
      ),
    })),
  [filteredRawData, lookupForProvider]);
  const posClv = useMemo(() => clvByPosition(tendencyPicks), [tendencyPicks]);
  const roundBandsClv = useMemo(() => clvByRoundBand(tendencyPicks), [tendencyPicks]);
  const maxPosAbs = Math.max(...posClv.map(t => Math.abs(t.net)), 1);
  const maxBandAbs = Math.max(...roundBandsClv.map(b => Math.abs(b.net)), 1);

  // ── Gaps ────────────────────────────────────────────────────────────
  const playersData = useMemo(() => groupDataByPlayer(filteredRawData), [filteredRawData]);

  // Your exposure (0–100) keyed by normalized player name.
  const exposureByName = useMemo(() => {
    const totalDrafts = new Set(filteredRawData.map(r => r["Draft"])).size || 1;
    const m = new Map<string, number>();
    for (const p of playersData) {
      const drafts = new Set(p.rows.map(r => r["Draft"])).size;
      m.set(normalizePlayerName(p.name), (drafts / totalDrafts) * 100);
    }
    return m;
  }, [playersData, filteredRawData]);

  // Premium Fades: early-ADP players you rarely or never roster. Uses ADP to
  // define "premium" and YOUR real exposure — no field-ownership estimate.
  const premiumFades = useMemo(() => {
    return adpData
      .filter(e => typeof e.adp === "number" && e.adp <= PREMIUM_ADP_MAX)
      .map(e => ({
        name: e.name,
        team: e.team ?? "",
        position: e.position ?? "",
        adp: e.adp,
        exposure: exposureByName.get(normalizePlayerName(e.name)) ?? 0,
      }))
      .sort((a, b) => a.exposure - b.exposure || a.adp - b.adp)
      .slice(0, 12);
  }, [adpData, exposureByName]);

  // Missing Combos: high-exposure players you rarely roster together. Grouped
  // by the higher-exposure player so each "hub" appears once with the
  // teammates you rarely pair them with (no repeated rows).
  const [minComboExposure, setMinComboExposure] = useState(15);
  const comboGroups = useMemo(() => {
    const pairs = getMissingCombos(playersData, filteredRawData, minComboExposure, 200);
    type Partner = { name: string; team: string; position: string; togetherPct: number };
    const groups = new Map<string, { name: string; team: string; position: string; exposure: number; partners: Partner[] }>();
    for (const { p1, p2, exposure1, exposure2, togetherPct } of pairs) {
      const a = { name: p1.name, team: p1.team, position: p1.position, exposure: exposure1 };
      const b = { name: p2.name, team: p2.team, position: p2.position, exposure: exposure2 };
      const [hub, partner] = a.exposure > b.exposure || (a.exposure === b.exposure && a.name < b.name) ? [a, b] : [b, a];
      const g = groups.get(hub.name) ?? { ...hub, partners: [] };
      g.partners.push({ name: partner.name, team: partner.team, position: partner.position, togetherPct });
      groups.set(hub.name, g);
    }
    return Array.from(groups.values())
      .map(g => ({ ...g, partners: g.partners.sort((x, y) => x.togetherPct - y.togetherPct).slice(0, 10) }))
      .sort((x, y) => y.partners.length - x.partners.length || y.exposure - x.exposure)
      .slice(0, 8);
  }, [playersData, filteredRawData, minComboExposure]);

  // ── Tab sync via intersection observer ──────────────────────────────
  useEffect(() => {
    if (!hasData) return;
    const observers = sectionRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting && !suppressObserver.current) setActiveTab(i); },
        { threshold: 0.25 },
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, [hasData]);

  const scrollToSection = (i: number) => {
    suppressObserver.current = true;
    setActiveTab(i);
    sectionRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => { suppressObserver.current = false; }, 800);
  };

  if (!hasData) return <HeroEmptyState />;

  const pct = (r: number) => `${Math.round(r * 100)}%`;

  // ── Stack report data ──────────────────────────────────────────────
  // Group the detected game stacks by NFL team + QB; collect naked QBs.
  const entryLabelById = new Map<string, string>();
  for (const t of teams) {
    const parts = [t.tournament || "Draft"];
    if (t.draftSlot !== "" && t.draftSlot !== undefined) parts.push(`slot ${t.draftSlot}`);
    entryLabelById.set(t.draftId, parts.join(" · "));
  }

  // Playoff schedule (weeks 15-17) for a stack's NFL team, with dome flags.
  const playoffWeeksFor = (nflTeam: string): PlayoffWeek[] => {
    if (!schedule?.weekNums || !schedule.weekSchedules) return [];
    const sched = schedule.weekSchedules[nflTeam];
    if (!sched) return [];
    return schedule.weekNums
      .map((w, idx) => ({ w, idx }))
      .filter(({ w }) => w >= 15 && w <= 17)
      .map(({ w, idx }) => {
        const m = (sched[idx] ?? "").trim();
        const away = m.startsWith("@");
        const opp = (away ? m.slice(1) : m.toLowerCase().startsWith("vs") ? m.slice(2) : m).trim();
        if (!opp || opp.toUpperCase() === "BYE") return { week: w, label: "BYE", dome: false };
        const dome = DOME_STADIUMS.has(away ? opp.toUpperCase() : nflTeam);
        return { week: w, label: `${away ? "@" : "vs"} ${opp}`, dome };
      });
  };

  const teamById = new Map(teams.map(t => [t.draftId, t]));

  // Wk17 bring-backs you ACTUALLY built for a stack: players from the stack
  // team's week-17 opponent rostered on the same builds, counted.
  const wk17StackedFor = (nflTeam: string, entryIds: string[]): Wk17BringBack | null => {
    const wk17 = playoffWeeksFor(nflTeam).find(p => p.week === 17);
    if (!wk17 || wk17.label === "BYE") return null;
    const opp = wk17.label.replace(/^(vs|@)\s*/i, "").trim().toUpperCase();
    if (!opp) return null;
    const counts = new Map<string, { name: string; position: string; count: number }>();
    for (const id of entryIds) {
      const build = teamById.get(id);
      if (!build) continue;
      for (const p of build.players) {
        if ((p.team ?? "").toUpperCase() !== opp) continue;
        if (!["WR", "TE", "RB"].includes(p.position)) continue;
        const cur = counts.get(p.name) ?? { name: p.name, position: p.position, count: 0 };
        cur.count++;
        counts.set(p.name, cur);
      }
    }
    return {
      opp,
      dome: wk17.dome,
      players: [...counts.values()].sort((a, b) => b.count - a.count).slice(0, 3),
    };
  };

  const stackGroups: StackGroup[] = (() => {
    if (!stacks) return [];
    const groups = new Map<string, StackGroup>();
    for (const tr of stacks.teamResults) {
      for (const s of tr.stacks) {
        const qb = s.players.find(p => p.position === "QB");
        if (!qb) continue;
        const key = `${s.nflTeam}|${qb.name}`;
        const g = groups.get(key) ?? { nflTeam: s.nflTeam, qbName: qb.name, buildCount: 0, receivers: [], bringBacks: [], domeWeeks: [], entries: [] };
        if (s.type === "game_stack") {
          g.buildCount++;
          if (!g.entries.some(e => e.draftId === tr.draftId)) {
            g.entries.push({ draftId: tr.draftId, label: entryLabelById.get(tr.draftId) ?? "Draft" });
          }
          for (const p of s.players) {
            if (p.name === qb.name) continue;
            const r = g.receivers.find(x => x.name === p.name);
            if (r) r.count++;
            else g.receivers.push({ name: p.name, position: p.position, count: 1 });
          }
        } else if (s.type === "bring_back") {
          if (s.opposingTeam && !g.bringBacks.includes(s.opposingTeam)) g.bringBacks.push(s.opposingTeam);
          if (s.weekNum && !g.domeWeeks.includes(s.weekNum)) g.domeWeeks.push(s.weekNum);
        }
        groups.set(key, g);
      }
    }
    return [...groups.values()]
      .filter(g => g.buildCount > 0)
      .map(g => ({ ...g, receivers: g.receivers.sort((a, b) => b.count - a.count) }))
      .sort((a, b) => b.buildCount - a.buildCount);
  })();

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      {/* Sticky anchor-tab bar (same pattern as the dashboard) */}
      <Box sx={{
        position: "sticky", top: 0, zIndex: 10, bgcolor: "background.paper",
        borderBottom: 1, borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => scrollToSection(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44, "& .MuiTab-root": { minHeight: 44, fontSize: 13, py: 0 } }}
        >
          {SECTIONS.map(s => <Tab key={s.id} label={s.label} />)}
        </Tabs>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, display: "flex", flexDirection: "column", gap: 5 }}>

        {/* ── STACKS ─────────────────────────────────────────────── */}
        <Box ref={el => { sectionRefs.current[0] = el as HTMLDivElement | null; }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            How often you pair a QB with his own skill players (WR, TE, or RB) across your drafts.
          </Typography>

          {!hasTeamData ? (
            <AccentCard sx={{ p: 2.5 }}>
              <TeamDataUnavailable feature="Stacking" dense />
            </AccentCard>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 1.5, mb: 2.5 }}>
                <StatCard metric={stacks ? pct(stacks.gameStackRate) : "—"} label="teams with a game stack" accentColor={sc.good} />
                <StatCard metric={schedule && stacks ? pct(stacks.bringBackRate) : "—"} label={schedule ? "teams with a playoff bring-back" : "bring-back (needs schedule)"} accentColor={primary} />
                <StatCard metric={schedule && stacks ? pct(stacks.domeStackRate) : "—"} label={schedule ? "wk-17 dome bring-back" : "dome (needs schedule)"} accentColor={primary} />
              </Box>

              <AccentCard sx={{ p: 2.5 }}>
                <SectionHeader
                  icon={<LinkOutlinedIcon />}
                  title="Your stacks"
                  subtitle="Every QB stack in the portfolio, ranked by how many builds carry it. Click a stack for its playoff schedule, partners, bring-backs, and the builds behind it."
                />
                {stackGroups.length === 0 ? (
                  <EmptyState dense title="No QB stacks to report yet" hint="Draft a QB alongside one of his own skill players (WR, TE, or RB) to form a stack." />
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 4, alignItems: "start" }}>
                    {stackGroups.map(g => (
                      <StackRow
                        key={`${g.nflTeam}|${g.qbName}`}
                        g={g}
                        totalBuilds={stacks?.totalTeams ?? teams.length}
                        playoffWeeks={playoffWeeksFor(g.nflTeam)}
                        wk17bb={wk17StackedFor(g.nflTeam, g.entries.map(e => e.draftId))}
                        primary={primary}
                        onOpenPlayer={name => navigate(`/players?q=${encodeURIComponent(name)}`)}
                        onViewBuilds={qbName => navigate(`/teams?players=${encodeURIComponent(qbName)}`)}
                      />
                    ))}
                  </Box>
                )}
              </AccentCard>
            </>
          )}
        </Box>

        {/* ── CONSTRUCTION ───────────────────────────────────────── */}
        <Box ref={el => { sectionRefs.current[1] = el as HTMLDivElement | null; }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            How you build each position. RB and WR by how many you take in the first 5 rounds; QB and TE by when you take your first.
          </Typography>

          {!teams.length ? (
            <EmptyState dense title="No drafts to classify yet" />
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
              {positionDists.map(({ position, buckets }) => (
                <DistCard
                  key={position}
                  chip={<PositionChip position={position} size="sm" />}
                  title={POSITION_NAMES[position]}
                  rows={buckets}
                  max={Math.max(...buckets.map(b => b.count), 1)}
                  color={primary}
                />
              ))}
            </Box>
          )}

          {/* ── Your tendencies vs the market ─────────────────────── */}
          {(posClv.length > 0 || roundBandsClv.length > 0) && (
            <AccentCard sx={{ p: 2.5, mt: 2 }}>
              <SectionHeader
                title="Where your value comes from"
                subtitle="Net CLV by position and by round: which picks bank value against market ADP and which give it back. Descriptive, not advice."
              />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                {/* Net CLV by position */}
                <Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "text.secondary", mb: 1 }}>
                    Net CLV by position
                  </Typography>
                  {posClv.map(t => {
                    const pos = t.net >= 0;
                    const tone = pos ? sc.good : sc.bad;
                    const pctW = Math.min((Math.abs(t.net) / maxPosAbs) * 50, 50);
                    return (
                      <Box key={t.position} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.6 }}>
                        <PositionChip position={t.position} size="sm" />
                        <Box sx={{ flex: 1, position: "relative", height: 8 }}>
                          <Box sx={{ position: "absolute", inset: 0, borderRadius: 4, bgcolor: "action.hover" }} />
                          <Box sx={{ position: "absolute", left: "50%", top: -3, bottom: -3, width: "1.5px", bgcolor: "text.disabled" }} />
                          <Box sx={{
                            position: "absolute", top: 0, bottom: 0,
                            ...(pos
                              ? { left: "50%", width: `${pctW}%`, borderRadius: "0 4px 4px 0" }
                              : { right: "50%", width: `${pctW}%`, borderRadius: "4px 0 0 4px" }),
                            bgcolor: tone, opacity: 0.85,
                          }} />
                        </Box>
                        <Typography sx={{ width: 76, textAlign: "right", fontSize: 12, fontWeight: 700, color: tone, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                          {pos ? "+" : "−"}{Math.abs(t.net).toFixed(1)}
                          <Box component="span" sx={{ color: "text.disabled", fontWeight: 500, fontSize: 10 }}> · {t.n}</Box>
                        </Typography>
                      </Box>
                    );
                  })}
                  <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.75 }}>
                    Positive = your picks at that position landed later than their ADP (discounts). Count = picks with ADP data.
                  </Typography>
                </Box>

                {/* CLV by round band */}
                <Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "text.secondary", mb: 1 }}>
                    Net CLV by round
                  </Typography>
                  {roundBandsClv.map(b => {
                    const pos = b.net >= 0;
                    const tone = pos ? sc.good : sc.bad;
                    const pctW = Math.min((Math.abs(b.net) / maxBandAbs) * 50, 50);
                    return (
                      <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.6 }}>
                        <Typography sx={{ width: 52, fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                          {b.label}
                        </Typography>
                        <Box sx={{ flex: 1, position: "relative", height: 8 }}>
                          <Box sx={{ position: "absolute", inset: 0, borderRadius: 4, bgcolor: "action.hover" }} />
                          <Box sx={{ position: "absolute", left: "50%", top: -3, bottom: -3, width: "1.5px", bgcolor: "text.disabled" }} />
                          <Box sx={{
                            position: "absolute", top: 0, bottom: 0,
                            ...(pos
                              ? { left: "50%", width: `${pctW}%`, borderRadius: "0 4px 4px 0" }
                              : { right: "50%", width: `${pctW}%`, borderRadius: "4px 0 0 4px" }),
                            bgcolor: tone, opacity: 0.85,
                          }} />
                        </Box>
                        <Typography sx={{ width: 76, textAlign: "right", fontSize: 12, fontWeight: 700, color: tone, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                          {pos ? "+" : "−"}{Math.abs(b.net).toFixed(1)}
                          <Box component="span" sx={{ color: "text.disabled", fontWeight: 500, fontSize: 10 }}> · {b.n}</Box>
                        </Typography>
                      </Box>
                    );
                  })}
                  <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.75 }}>
                    Positive = picks in those rounds landed later than market ADP (discounts). Count = picks with ADP data.
                  </Typography>
                </Box>
              </Box>
            </AccentCard>
          )}
        </Box>

        {/* ── GAPS ───────────────────────────────────────────────── */}
        <Box ref={el => { sectionRefs.current[2] = el as HTMLDivElement | null; }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            What you're missing: early-round players you've barely drafted, and players you draft a lot but rarely on the same team.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Premium Fades */}
            <AccentCard accentColor={sc.warn} sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<TrendingDownOutlinedIcon />}
                title="Premium Fades"
                subtitle="Players going in the first 5 rounds that you rarely or never draft."
              />
              {premiumFades.length === 0 ? (
                <EmptyState dense title="No premium players found" hint="ADP data may still be loading." />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 4, rowGap: 0.75 }}>
                  {premiumFades.map(p => (
                    <Box key={p.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {p.position && <PositionChip position={p.position} size="sm" />}
                      <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography sx={{ minWidth: 0, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {p.name}
                        </Typography>
                        {p.team && <TeamBadge team={p.team} size="sm" />}
                      </Box>
                      <Typography variant="caption" sx={{ flexShrink: 0, color: "text.disabled", fontVariantNumeric: "tabular-nums" }}>
                        ADP {p.adp.toFixed(0)}
                      </Typography>
                      <Typography sx={{
                        width: 36, flexShrink: 0, textAlign: "right", fontWeight: 700, fontSize: 13, fontVariantNumeric: "tabular-nums",
                        color: p.exposure === 0 ? sc.bad : p.exposure <= 5 ? sc.warn : "text.primary",
                      }}>
                        {p.exposure.toFixed(0)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </AccentCard>

            {/* Missing Combos */}
            <AccentCard sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<JoinInnerOutlinedIcon />}
                title="Missing Combos"
                subtitle="Players you draft a lot on their own but rarely together."
                action={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 150 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                      min {minComboExposure}%
                    </Typography>
                    <Slider
                      size="small"
                      value={minComboExposure}
                      min={5}
                      max={40}
                      step={5}
                      onChange={(_, v) => setMinComboExposure(v as number)}
                      aria-label="Minimum per-player exposure"
                      sx={{ width: 90 }}
                    />
                  </Box>
                }
              />
              {comboGroups.length === 0 ? (
                <EmptyState dense title="No missing combos" hint="Lower the min exposure, or you're pairing your core players well." />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {comboGroups.map(g => (
                    <Box key={g.name} sx={{ pb: 1.25, borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": { borderBottom: "none", pb: 0 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                        <PositionChip position={g.position} size="sm" />
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{g.name}</Typography>
                        {g.team && <TeamBadge team={g.team} size="sm" />}
                        <Typography variant="caption" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                          {g.exposure.toFixed(0)}% exposure
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, pl: 0.5 }}>
                        <Typography variant="caption" sx={{ color: "text.disabled", flexShrink: 0 }}>rarely with</Typography>
                        {g.partners.map(pt => (
                          <Box key={pt.name} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: "action.selected", borderRadius: "8px", pl: 0.5, pr: 0.9, py: 0.3 }}>
                            <PositionChip position={pt.position} size="sm" />
                            <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{pt.name}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </AccentCard>
          </Box>
        </Box>

        {/* ── DUPLICATION ────────────────────────────────────────── */}
        <Box ref={el => { sectionRefs.current[3] = el as HTMLDivElement | null; }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Near-identical entries in the same tournament split their own equity — they boom and bust
            together and can knock each other out of the same advance group.
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2.5 }}>
            <StatCard metric={String(dupReport.totalPairs)} label="same-tournament pairs" accentColor={primary} />
            <StatCard metric={String(dupReport.maxShared)} label="most shared players" accentColor={primary} />
            <StatCard metric={String(dupReport.highPairs)} label="high-overlap pairs (7+)" accentColor={dupReport.highPairs > 0 ? sc.warn : sc.good} />
            <StatCard metric={String(dupReport.clonePairs)} label="near-clones (10+)" accentColor={dupReport.clonePairs > 0 ? sc.bad : sc.good} />
          </Box>

          <AccentCard sx={{ p: 2.5, mb: 2 }}>
            <SectionHeader
              icon={<JoinInnerOutlinedIcon />}
              title="Uniqueness by tournament"
              subtitle="Average share of the roster your own entries have in common, per tournament. Typical BBM drafters average ~11%; the marker shows that benchmark."
            />
            {tournamentUniqueness.length === 0 ? (
              <EmptyState dense title="No multi-entry tournaments" hint="Uniqueness needs at least two entries in the same tournament." />
            ) : (
              <Box>
                {tournamentUniqueness.map(u => {
                  const pctNum = u.meanOverlapPct * 100;
                  const color = pctNum >= 30 ? sc.bad : pctNum >= 18 ? sc.warn : sc.good;
                  return (
                    <Box key={u.tournament} sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", width: 200, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {u.tournament} · {u.entryCount} entries
                      </Typography>
                      <Box sx={{ flex: 1, height: 12, bgcolor: `${color}22`, borderRadius: "3px", overflow: "hidden", position: "relative" }}>
                        <Box sx={{ height: "100%", width: `${Math.min(100, pctNum)}%`, bgcolor: color, borderRadius: "3px" }} />
                        <Box sx={{ position: "absolute", top: 0, bottom: 0, left: "11%", width: "2px", bgcolor: "text.disabled", opacity: 0.7 }} />
                      </Box>
                      <Typography variant="caption" sx={{ minWidth: 132, textAlign: "right", color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                        {pctNum.toFixed(0)}% avg · worst {u.maxShared}/{u.rosterSize}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </AccentCard>

          <AccentCard accentColor={dupReport.clonePairs > 0 ? sc.bad : undefined} sx={{ p: 2.5 }}>
            <SectionHeader
              icon={<JoinInnerOutlinedIcon />}
              title="Duplicated build clusters"
              subtitle={`Entries in the same tournament grouped when they share ${minDupShared}+ players. Typical drafters share ~2 between their own builds; 5 is already the top 5% of real BBM portfolios.`}
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 150 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                    min {minDupShared} shared
                  </Typography>
                  <Slider
                    size="small"
                    value={minDupShared}
                    min={3}
                    max={12}
                    step={1}
                    onChange={(_, v) => setMinDupShared(v as number)}
                    aria-label="Minimum shared players for a cluster"
                    sx={{ width: 90 }}
                  />
                </Box>
              }
            />
            {dupClusters.length === 0 && dupReport.pairs.length === 0 ? (
              <EmptyState dense title="No overlapping entries" hint="Upload a portfolio with two or more entries in the same tournament to measure duplication." />
            ) : dupClusters.length === 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                  No high-overlap clusters — your closest pairs for reference:
                </Typography>
                {dupReport.pairs.slice(0, 5).map(pr => (
                  <DupOverlapBar
                    key={`${pr.a.draftId}|${pr.b.draftId}`}
                    label={`slot ${pr.a.draftSlot} vs slot ${pr.b.draftSlot} · ${pr.tournament}`}
                    shared={pr.shared}
                    rosterSize={pr.rosterSize}
                    color={primary}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {dupClustersByTournament.map(([tournament, clusters], tIndex) => (
                  <Box key={tournament}>
                    <Box
                      onClick={() => toggleDup(tournament, tIndex)}
                      sx={{ display: "flex", alignItems: "center", gap: 1, mb: isDupExpanded(tournament, tIndex) ? 1.25 : 0, pb: 0.5, borderBottom: "2px solid", borderColor: "divider", cursor: "pointer", userSelect: "none", "&:hover": { borderColor: "text.disabled" } }}
                    >
                      <ExpandMoreIcon
                        sx={{ fontSize: 18, color: "text.secondary", transform: isDupExpanded(tournament, tIndex) ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 120ms" }}
                      />
                      <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        {tournament}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {clusters.length} {clusters.length === 1 ? "cluster" : "clusters"} · {clusters.reduce((s2, c) => s2 + c.entries.length, 0)} builds
                        {(() => { const m = Math.max(...clusters.map(c => c.coreNames.length)); return ` · biggest core ${m}`; })()}
                      </Typography>
                    </Box>
                    <Collapse in={isDupExpanded(tournament, tIndex)} timeout={150} unmountOnExit>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 0.5 }}>
                      {clusters.map(cl => {
                  const sev = cl.coreNames.length >= 10 ? sc.bad : cl.coreNames.length >= 7 ? sc.warn : primary;
                  return (
                    <Box key={cl.entries.map(e => e.draftId).join("|")} sx={{ pb: 2, borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": { borderBottom: "none", pb: 0 } }}>
                      <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1, mb: 0.75 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {cl.entries.length} builds share a {cl.coreNames.length}-player core
                        </Typography>
                        <Typography variant="caption" sx={{ color: sev, fontWeight: 700 }}>
                          {cl.coreNames.length >= 10 ? "near-clones" : cl.coreNames.length >= 7 ? "high overlap" : "correlated core"}
                        </Typography>
                        <Typography
                          variant="caption"
                          onClick={() => navigate(`/teams?players=${encodeURIComponent(cl.coreNames.slice(0, 2).join(","))}`)}
                          sx={{ color: primary, fontWeight: 600, cursor: "pointer", ml: "auto", "&:hover": { textDecoration: "underline" } }}
                        >
                          view builds →
                        </Typography>
                      </Box>
                      <DupOverlapBar
                        label={`core ${cl.coreNames.length} of ${cl.rosterSize}`}
                        shared={cl.coreNames.length}
                        rosterSize={cl.rosterSize}
                        color={sev}
                      />
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                        {cl.coreNames.map(n => (
                          <Box key={n} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: "action.selected", borderRadius: "8px", pl: 0.5, pr: 0.9, py: 0.3 }}>
                            {positionByName.get(n) && <PositionChip position={positionByName.get(n) as string} size="sm" />}
                            <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{n}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                        {cl.entries.map(e => (
                          <Typography key={e.draftId} variant="caption" sx={{ color: "text.secondary", bgcolor: "action.hover", borderRadius: "6px", px: 0.75, py: 0.25 }}>
                            slot {e.draftSlot} · {e.date}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
                    </Box>
                    </Collapse>
                  </Box>
                ))}
              </Box>
            )}
          </AccentCard>
        </Box>
      </Box>
    </Box>
  );
}

// ── Duplication overlap bar (shared portion of the roster) ──────────
function DupOverlapBar({ label, shared, rosterSize, color }: {
  label: string; shared: number; rosterSize: number; color: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", width: 170, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 12, bgcolor: `${color}22`, borderRadius: "3px", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${Math.min(100, (shared / Math.max(1, rosterSize)) * 100)}%`, bgcolor: color, borderRadius: "3px" }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 52, textAlign: "right", color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
        {shared}/{rosterSize}
      </Typography>
    </Box>
  );
}

// ── Distribution card (label + bar + count/rate) ────────────────────
function DistCard({ title, subtitle, chip, rows, max, color, empty }: {
  title: string;
  subtitle?: string;
  chip?: React.ReactNode;
  rows: Array<{ label: string; count: number; rate: number }>;
  max: number;
  color: string;
  empty?: string;
}) {
  return (
    <AccentCard sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        {chip}
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: "text.secondary" }}>{subtitle}</Typography>}
      </Box>
      {rows.length === 0 ? (
        <Typography variant="caption" sx={{ color: "text.disabled" }}>{empty ?? "No data"}</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {rows.map(d => (
            <Box key={d.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={POSITION_ARCHETYPE_DESCRIPTIONS[d.label] ?? ""} arrow placement="top">
                <Typography
                  sx={{
                    fontSize: 12, fontWeight: 500, width: 88, flexShrink: 0,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    cursor: "help",
                    textDecoration: "underline dotted",
                    textDecorationColor: "rgba(128,128,128,0.5)",
                    textUnderlineOffset: 3,
                  }}
                >
                  {d.label}
                </Typography>
              </Tooltip>
              <Box sx={{ flex: 1, height: 12, bgcolor: `${color}22`, borderRadius: "3px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(d.count / max) * 100}%`, bgcolor: color, borderRadius: "3px" }} />
              </Box>
              <Typography variant="caption" sx={{ minWidth: 52, textAlign: "right", color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                {d.count} · {Math.round(d.rate * 100)}%
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </AccentCard>
  );
}
